import type echarts from '@/utils/echartsCore';

export const CHART_IMAGE_DOWNLOAD_FEATURE = 'myChartImageDownload';

const HORIZONTAL_PADDING = 32;
const TOP_PADDING = 24;
const TITLE_DESCRIPTION_GAP = 8;
const DESCRIPTION_CHART_GAP = 16;
const BOTTOM_PADDING = 24;
const TITLE_FONT = '600 18px Rubik, sans-serif';
const DESCRIPTION_FONT = '400 13px Rubik, sans-serif';
const TITLE_LINE_HEIGHT = 24;
const DESCRIPTION_LINE_HEIGHT = 18;
const TITLE_COLOR = '#333333';
const DESCRIPTION_COLOR = '#666666';
const BACKGROUND_COLOR = '#ffffff';
const SAVE_IMAGE_ICON = 'path://M4.7,22.9L29.3,45.5L54.7,23.4M4.6,43.6L4.6,58L53.8,58L53.8,43.6M29.2,45.1L29.2,0';

function sanitizeChartImageFilename(name: string): string {
    const sanitized = name.trim().replace(/[^\w\- ]+/g, '').replace(/\s+/g, '_').slice(0, 100);
    return sanitized || 'chart';
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
    });
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    if (!words.length) {
        return [];
    }

    const lines: string[] = [];
    let currentLine = words[0];

    for (let index = 1; index < words.length; index += 1) {
        const candidate = `${currentLine} ${words[index]}`;
        if (ctx.measureText(candidate).width <= maxWidth) {
            currentLine = candidate;
        } else {
            lines.push(currentLine);
            currentLine = words[index];
        }
    }

    lines.push(currentLine);
    return lines;
}

function drawCenteredLines(
    ctx: CanvasRenderingContext2D,
    lines: string[],
    centerX: number,
    startY: number,
    lineHeight: number,
    baselineOffset: number,
): number {
    let y = startY;
    lines.forEach((line) => {
        ctx.fillText(line, centerX, y + baselineOffset);
        y += lineHeight;
    });
    return y;
}

export async function downloadChartImageWithMetadata(
    chartInstance: echarts.ECharts,
    options: {
        title: string;
        description?: string;
        datePeriod?: string;
        filename?: string;
    },
): Promise<void> {
    const chartDataUrl = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: BACKGROUND_COLOR,
        excludeComponents: ['toolbox'],
    });

    const chartImage = await loadImage(chartDataUrl);
    const exportWidth = chartImage.width;
    const contentWidth = Math.max(120, exportWidth - HORIZONTAL_PADDING * 2);

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    if (!measureCtx) {
        return;
    }

    const trimmedTitle = options.title.trim();
    const trimmedDescription = options.description?.trim() ?? '';
    const trimmedDatePeriod = options.datePeriod?.trim() ?? '';

    measureCtx.font = TITLE_FONT;
    const titleLines = trimmedTitle ? wrapText(measureCtx, trimmedTitle, contentWidth) : [];

    measureCtx.font = DESCRIPTION_FONT;
    const descriptionLines = trimmedDescription
        ? wrapText(measureCtx, trimmedDescription, contentWidth)
        : [];
    const datePeriodLines = trimmedDatePeriod
        ? wrapText(measureCtx, trimmedDatePeriod, contentWidth)
        : [];

    let headerHeight = TOP_PADDING;
    let hasMetadata = false;

    if (titleLines.length) {
        headerHeight += titleLines.length * TITLE_LINE_HEIGHT;
        hasMetadata = true;
    }
    if (descriptionLines.length) {
        if (hasMetadata) {
            headerHeight += TITLE_DESCRIPTION_GAP;
        }
        headerHeight += descriptionLines.length * DESCRIPTION_LINE_HEIGHT;
        hasMetadata = true;
    }
    if (datePeriodLines.length) {
        if (hasMetadata) {
            headerHeight += TITLE_DESCRIPTION_GAP;
        }
        headerHeight += datePeriodLines.length * DESCRIPTION_LINE_HEIGHT;
        hasMetadata = true;
    }
    if (hasMetadata) {
        headerHeight += DESCRIPTION_CHART_GAP;
    }

    const canvas = document.createElement('canvas');
    canvas.width = exportWidth;
    canvas.height = headerHeight + chartImage.height + BOTTOM_PADDING;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    ctx.fillStyle = BACKGROUND_COLOR;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const centerX = exportWidth / 2;
    let y = TOP_PADDING;

    if (titleLines.length) {
        ctx.fillStyle = TITLE_COLOR;
        ctx.font = TITLE_FONT;
        ctx.textAlign = 'center';
        y = drawCenteredLines(ctx, titleLines, centerX, y, TITLE_LINE_HEIGHT, 16);
    }

    if (descriptionLines.length) {
        if (titleLines.length) {
            y += TITLE_DESCRIPTION_GAP;
        }
        ctx.fillStyle = DESCRIPTION_COLOR;
        ctx.font = DESCRIPTION_FONT;
        y = drawCenteredLines(ctx, descriptionLines, centerX, y, DESCRIPTION_LINE_HEIGHT, 14);
    }

    if (datePeriodLines.length) {
        if (titleLines.length || descriptionLines.length) {
            y += TITLE_DESCRIPTION_GAP;
        }
        ctx.fillStyle = DESCRIPTION_COLOR;
        ctx.font = DESCRIPTION_FONT;
        drawCenteredLines(ctx, datePeriodLines, centerX, y, DESCRIPTION_LINE_HEIGHT, 14);
    }

    ctx.drawImage(chartImage, 0, headerHeight);

    const link = document.createElement('a');
    link.download = `${sanitizeChartImageFilename(options.filename ?? options.title)}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
}

export function buildChartImageDownloadToolboxFeatures(
    options: {
        title: string;
        description?: string;
        datePeriod?: string;
        getChartInstance: () => echarts.ECharts | undefined | null;
    },
    show: boolean,
): Record<string, unknown> {
    return {
        saveAsImage: {show: false},
        [CHART_IMAGE_DOWNLOAD_FEATURE]: {
            show,
            title: 'Save as image',
            icon: SAVE_IMAGE_ICON,
            onclick: () => {
                const chartInstance = options.getChartInstance();
                if (!chartInstance || chartInstance.isDisposed()) {
                    return;
                }

                void downloadChartImageWithMetadata(chartInstance, {
                    title: options.title,
                    description: options.description,
                    datePeriod: options.datePeriod,
                });
            },
        },
    };
}
